/**
 * @name News
 * @description Componente que muestra los avisos de la empresa
 * @author Ricardo Guerra
 */

import React, { PureComponent } from 'react';
import i18n from 'i18next';
import styled from 'styled-components';
import moment from 'moment';
import { trackPromise } from 'react-promise-tracker';

import { LoadingWrapper } from '@components';
import { NewsService, ResponseHandlerService } from '@services';
import { Flex, Card, MUI, Unicons, Typography } from '@styles';

import NewsDialog from '../dialogs/news.dialog';
import NewsImagesDialog from '../dialogs/news-image.dialog';

class News extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      newsData: [],
      open: false,
      openImage: false,
      newsSelected: null,
      filters: {
        read: false
      }
    };
  }

  componentDidMount() {
    const { filters } = this.state;
    this.getNews(filters);
  }

  getNews = (filters) => {
    trackPromise(
      NewsService.getByUser({ filter: { leido: filters.read } })
        .then((response) => {
          this.setState({ newsData: response });
        })
        .catch((error) => {
          ResponseHandlerService.error(error);
          this.setState({ newsData: [] });
        }),
      'loading-news'
    );
  }

  filterNews = (filters) => {
    this.getNews(filters);
    this.setState({ filters });
  }

  clickRow = (row) => {
    const { filters } = this.state;

    let advice = row[0];
    advice.Leido = !advice.Leido;
    NewsService.markAsRead(advice)
      .then((response) => {
        ResponseHandlerService.success(response);
        this.getNews(filters);
      })
      .catch((error) => {
        ResponseHandlerService.error(error);
      });
  }

  openDialog = () => {
    this.setState({ open: true });
  }

  closeDialog = () => {
    this.setState({ open: false });
  }

  openImageDialog = (newSelected) => {
    this.setState({ openImage: true, newsSelected: newSelected });
  }

  closeImageDialog = () => {
    this.setState({ openImage: false });
  }

  render() {
    const {
      newsData,
      filters,
      open,
      openImage,
      newsSelected
    } = this.state;

    return (
      <>
        {/* HEADER */}
        <Card.Header divider>
          <Card.HeaderTitle>
            <Typography.H5>{i18n.t('News.news')}</Typography.H5>
            <Typography.Text color="greyDarkAlpha">{i18n.t('News.importantAnnouncements')}</Typography.Text>
          </Card.HeaderTitle>
          <Card.HeaderAction>
            <MUI.Tooltip title={i18n.t('GenericMessages.seeAll')}>
              <MUI.ButtonIcon onClick={this.openDialog}>
                <Unicons.EllipsisH size="20" />
              </MUI.ButtonIcon>
            </MUI.Tooltip>
          </Card.HeaderAction>
        </Card.Header>
        {/* CONTENT */}
        <Card.Content scroll>
          <LoadingWrapper area="loading-news">
            {newsData.map((news, index) => {
              return (
                <React.Fragment key={index}>
                  <Styled.News>
                    {/* Image - Link */}
                    {news.URLArchivo && (
                      <Styled.LinkImage onClick={() => this.openImageDialog(news)}>
                        <Styled.Image src={news.URLArchivo} />
                        {/* Button - Link */}
                        <Styled.ExternalLink size="20" />
                      </Styled.LinkImage>
                    )}
                    {/* Image - Default */}
                    {!news.URLArchivo && (
                      <Styled.DefaultImage onClick={() => this.openImageDialog(news)}>
                        <Styled.ImageDefault src={'https://assets.talentry.io/icons/default/news-logo.png'} />
                        {/* Button - Link */}
                        <Styled.ExternalLink size="20" />
                      </Styled.DefaultImage>
                    )}
                    <Styled.NewsContent>
                      {/* Título & Fecha */}
                      <Flex.Col flex="1">
                        <Flex.Row alignItems="flex-start" padding="8px">
                          <Typography.SubHeading overflow={2} style={{ flex: '1' }}>{news.Titulo}</Typography.SubHeading>
                          <Typography.Text color="greyDarkAlpha" style={{ marginLeft: '12px' }}>{moment(news.FechaInicio).format(i18n.t('FormatDates.short'))}</Typography.Text>
                        </Flex.Row>
                        {/* Descripción */}
                        <Flex.Row padding="0 8px 8px">
                          <Typography.Text overflow={3} style={{ flex: '1' }}>{news.Mensaje}</Typography.Text>
                        </Flex.Row>
                      </Flex.Col>
                    </Styled.NewsContent>
                  </Styled.News>
                  {(newsData.length - 1) > (index) && (
                    <MUI.Divider key={index} color="greyHover" margin="0 8px 8px" />
                  )}
                </React.Fragment>
              );
            })}
            {/* News - Not Found */}
            {newsData.length === 0 && (
              <Styled.BoxNotFound>
                <Unicons.CircleExclamation size="20" style={{ marginRight: '12px' }} />
                <Typography.H6>{`${i18n.t('News.noNews')}`}</Typography.H6>
              </Styled.BoxNotFound>
            )}
          </LoadingWrapper>
        </Card.Content>

        {/* MODAL - News */}
        {this.state.open && (
          <NewsDialog
            open={open}
            news={newsData}
            filters={filters}
            onClose={this.closeDialog}
            onFilter={this.filterNews}
            onClickRow={this.clickRow}
          />
        )}

        {/* MODAL - News Image */}
        {this.state.openImage && (
          <NewsImagesDialog
            open={openImage}
            onClose={this.closeImageDialog}
            newSelected={newsSelected}
          />
        )}
      </>
    );
  }
}

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.News = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 116px;
  padding: 8px 8px 16px 8px;
  overflow: hidden;

  &:last-of-type { 
    margin: 0px; 
    padding: 8px;
  }
  
  @media (max-width: 800px) { 
    flex-wrap: wrap;
  }
`;

Styled.NewsContent = styled.div`
  display: flex;
  width: 100%;
  height: 116px;
  margin-left: 235px;
  padding-left: 8px;

  @media (max-width: 800px) { 
    height: 100%;
    margin-top: 8px;
    margin-left: 0; 
    padding-left: 0;
  }
`;

Styled.ExternalLink = styled(Unicons.ExternalLink)`
  position: absolute;
  color: inherit;
  right: 16px;
  top: 16px;
`;

Styled.LinkImage = styled.div`
  position: absolute;
  width: 235px;
  height: 116px;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: none;
    box-shadow: ${(props) => props.theme.shadows.depth2};
    
    ${Styled.ExternalLink} {
      color: ${(props) => props.theme.colors.blueMedium};
    }
  }

  @media (max-width: 800px) {
    position: relative;
    width: 100%;
    height: 100px;
  }
`;

Styled.DefaultImage = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 235px;
  height: 116px;
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.colors.greyLight};
  background-color: ${(props) => props.theme.colors.greyHover};
  cursor: pointer;

  &:hover {
    box-shadow: ${(props) => props.theme.shadows.depth2};
    ${Styled.ButtonIcon} {
      color: ${(props) => props.theme.colors.blueMedium};
    }
  }

  @media (max-width: 800px) {
    position: relative;
    width: 100%;
    height: 100px;
  }
`;

Styled.Image = styled.img`
  height: 100%;
  width: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 6px;
`;

Styled.ImageDefault = styled.img`
  height: 70px;
  width: auto;
  object-fit: cover;
  object-position: center;
`;

Styled.BoxNotFound = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;

  .MuiSvgIcon-root {
    margin-right: 12px;
    font-size: 20px;
  }
`;

export default News;